import React, { useState, useEffect } from 'react';
import { Shield, Play, Square, RefreshCw, CheckCircle, XCircle, Database, Users, FileText, Activity, Clock, Eye, Wrench, Settings, Search } from 'lucide-react';
import batchManagementService from '../../../services/batchManagementService';
import { supabase } from '../../../lib/supabase';

export default function ImmediateFixPanel() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFrozen, setUploadFrozen] = useState(null);
  const [counts, setCounts] = useState(null);
  const [countsHistory, setCountsHistory] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step3Status, setStep3Status] = useState('waiting'); // waiting, monitoring, success, error
  
  // Enhanced verification states
  const [verificationResults, setVerificationResults] = useState({
    workerStatus: 'unknown',
    batchQueued: null,
    promoterHealth: null,
    endToEndTest: null,
    watchdogResults: null,
    unfreezeReady: false
  });
  
  // Specific batch for testing (MediaArrestLogDatabase.csv)
  const [targetBatch, setTargetBatch] = useState(null);
  const [batchSanityChecks, setBatchSanityChecks] = useState(null);
  const [endToEndResults, setEndToEndResults] = useState(null);

  useEffect(() => {
    checkUploadStatus();
    fetchCurrentCounts();
    findTargetBatch();
  }, []);

  useEffect(() => {
    let interval;
    if (isMonitoring) {
      interval = setInterval(() => {
        fetchCurrentCounts();
      }, 3000); // Check every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMonitoring]);

  const checkUploadStatus = async () => {
    try {
      const result = await batchManagementService?.isUploadFrozen();
      if (result?.success) {
        setUploadFrozen(result?.data);
      }
    } catch (err) {
      setError('Failed to check upload status: ' + err?.message);
    }
  };

  // Find MediaArrestLogDatabase.csv batch for testing
  const findTargetBatch = async () => {
    try {
      const { data: batches } = await supabase
        ?.from('upload_batches')
        ?.select('id, filename, status, updated_at')
        ?.like('filename', '%MediaArrestLogDatabase%')
        ?.order('created_at', { ascending: false })
        ?.limit(1);

      if (batches?.length > 0) {
        setTargetBatch(batches?.[0]);
      }
    } catch (err) {
      console.warn('Could not find target batch:', err);
    }
  };

  // Step 0: Worker Process Verification
  const verifyWorkerProcess = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // A) Check if worker is up by checking system health
      const healthResult = await batchManagementService?.getProcessingHealthSummary();
      if (!healthResult?.success) {
        throw new Error('Worker health check failed: ' + healthResult?.error);
      }
      
      const health = healthResult?.data;
      const workerRunning = health?.processingBatches > 0 || health?.queuedBatches > 0;
      
      // B) Check if worker connected to same DB by verifying recent activity
      const recentActivity = await batchManagementService?.getRecentActivity(1);
      const hasRecentActivity = recentActivity?.data?.length > 0;
      
      setVerificationResults(prev => ({
        ...prev,
        workerStatus: workerRunning && hasRecentActivity ? 'running' : 'stopped'
      }));
      
      if (workerRunning && hasRecentActivity) {
        setSuccess('✅ Step 0: Worker process verified as running and connected to database');
      } else {
        setError('❌ Step 0: Worker process appears stopped or disconnected from database');
      }
    } catch (err) {
      setError('Step 0 failed: ' + err?.message);
      setVerificationResults(prev => ({
        ...prev,
        workerStatus: 'error'
      }));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Requeue Known Big Batch
  const requeueTargetBatch = async () => {
    if (!targetBatch) {
      setError('No target batch found. Looking for MediaArrestLogDatabase.csv...');
      await findTargetBatch();
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Requeue the specific batch using the exact SQL from the user request
      const { data, error } = await supabase?.rpc('custom_requeue_batch', {
        batch_filename: targetBatch?.filename
      });
      
      if (error) {
        // Fallback to manual update if RPC doesn't exist
        const { error: updateError } = await supabase
          ?.from('upload_batches')
          ?.update({ 
            status: 'queued', 
            updated_at: new Date()?.toISOString(),
            error_text: null 
          })
          ?.eq('id', targetBatch?.id);
          
        if (updateError) throw updateError;
      }
      
      setVerificationResults(prev => ({
        ...prev,
        batchQueued: targetBatch
      }));
      
      setSuccess(`✅ Step 1: Requeued batch ${targetBatch?.filename} successfully`);
      
      // Restart worker to pick up queued batch
      await restartWorker();
    } catch (err) {
      setError('Step 1 failed: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Watch for Deltas (Enhanced monitoring)
  const fetchCurrentCounts = async () => {
    try {
      setStep3Status('monitoring');
      
      // Use the BatchManagementService method for resilient count fetching
      const result = await batchManagementService?.getSystemHealthCounts();
      
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to fetch counts');
      }
      
      const currentCounts = {
        timestamp: new Date(),
        personsAll: result?.data?.persons_all || 0,
        arrestsAll: result?.data?.arrests_all || 0,
        arresteeRecordsAll: result?.data?.arrestee_records_all || 0,
        ingestRawAll: result?.data?.ingest_raw_all || 0,
        uploadProcessing: result?.data?.upload_processing || 0,
        uploadCompleted: result?.data?.upload_completed || 0
      };
      
      setCounts(currentCounts);
      
      // Add to history (keep last 20 entries)
      setCountsHistory(prev => [
        ...prev?.slice(-19),
        currentCounts
      ]);
      
      setStep3Status('success');
    } catch (err) {
      setError('Step-2 monitoring: ' + err?.message);
      setStep3Status('error');
      
      // Show fallback counts to prevent UI crash
      const fallbackCounts = {
        timestamp: new Date(),
        personsAll: 0,
        arrestsAll: 0,
        arresteeRecordsAll: 0,
        ingestRawAll: 0,
        uploadProcessing: 0,
        uploadCompleted: 0,
        error: err?.message
      };
      setCounts(fallbackCounts);
    }
  };

  // Step 3: Quick Promoter Sanity Checks
  const runPromoterSanityChecks = async () => {
    if (!targetBatch) {
      setError('No target batch selected for sanity checks');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // A) Check for missing datetime/facility mapping
      const { data: rawSample, error } = await supabase
        ?.from('ingest_raw')
        ?.select('*')
        ?.eq('batch_id', targetBatch?.id)
        ?.limit(100);
        
      if (error) throw error;
      
      const totalRows = rawSample?.length || 0;
      const noDatetime = rawSample?.filter(row => 
        !row?.booking_datetime && !row?.arrest_datetime
      )?.length || 0;
      
      const badName = rawSample?.filter(row => 
        !row?.first_name?.trim() || !row?.last_name?.trim()
      )?.length || 0;
      
      const noFacilityName = rawSample?.filter(row => 
        !row?.facility_name?.trim()
      )?.length || 0;
      
      const sanityResults = {
        totalRows,
        noDatetime,
        badName,
        noFacilityName,
        datetimeHealthy: noDatetime < (totalRows * 0.5),
        nameHealthy: badName < (totalRows * 0.5),
        facilityHealthy: noFacilityName < (totalRows * 0.8)
      };
      
      setBatchSanityChecks(sanityResults);
      
      setVerificationResults(prev => ({
        ...prev,
        promoterHealth: sanityResults
      }));
      
      const healthy = sanityResults?.datetimeHealthy && sanityResults?.nameHealthy;
      if (healthy) {
        setSuccess('✅ Step 3: Promoter sanity checks passed - data quality acceptable');
      } else {
        setError('⚠️ Step 3: Data quality issues found - review datetime/name mappings');
      }
    } catch (err) {
      setError('Step 3 failed: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: End-to-End Single Arrest Test
  const runEndToEndTest = async () => {
    if (!targetBatch) {
      setError('No target batch selected for end-to-end test');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Test promotion functions directly
      const personsResult = await batchManagementService?.promoteBatchPersons(targetBatch?.id);
      const arrestsResult = await batchManagementService?.promoteBatchArrests(targetBatch?.id);
      
      const testResults = {
        personsCreated: personsResult?.data?.persons_created || 0,
        personsExisting: personsResult?.data?.persons_existing || 0,
        arrestsCreated: arrestsResult?.data?.arrests_created || 0,
        arrestsExisting: arrestsResult?.data?.arrests_existing || 0,
        promotionWorking: (personsResult?.success && arrestsResult?.success)
      };
      
      setEndToEndResults(testResults);
      
      setVerificationResults(prev => ({
        ...prev,
        endToEndTest: testResults
      }));
      
      if (testResults?.promotionWorking) {
        setSuccess('✅ Step 4: End-to-end promotion test successful');
      } else {
        setError('❌ Step 4: Promotion functions failed - check promoter logic');
      }
    } catch (err) {
      setError('Step 4 failed: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Database-trusted Step-3 Monitor (already implemented)
  const verifyStep3Monitor = () => {
    const endpointWorking = step3Status === 'success';
    const returnsJson = counts && !counts?.error;
    const nonZeroValues = counts && (
      counts?.personsAll > 0 || 
      counts?.arrestsAll > 0 || 
      counts?.arresteeRecordsAll > 0
    );
    
    if (endpointWorking && returnsJson && nonZeroValues) {
      setSuccess('✅ Step 5: Step-3 monitor verified - returns JSON with non-zero values');
    } else {
      setError('❌ Step 5: Step-3 monitor issues - endpoint or data problems');
    }
  };

  // Step 6: Safe Unfreeze After Verification
  const safeUnfreeze = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Verify counts are moving before unfreezing
      const isCountsMoving = await verifyCountsMoving();
      if (!isCountsMoving) {
        throw new Error('Cannot unfreeze: Counts are not moving. Complete steps 0-4 first.');
      }
      
      // Check watchdog for stuck batches
      const watchdogResult = await batchManagementService?.runWatchdog();
      const stuckBatches = watchdogResult?.data?.length || 0;
      
      if (stuckBatches > 0) {
        throw new Error(`Cannot unfreeze: ${stuckBatches} stuck batches found. Resolve these first.`);
      }
      
      // All checks passed - safe to unfreeze
      const result = await batchManagementService?.setUploadFreeze(false);
      if (result?.success) {
        setUploadFrozen(false);
        setVerificationResults(prev => ({
          ...prev,
          unfreezeReady: true
        }));
        setSuccess('✅ Step 6: Uploads safely unfrozen - all verification checks passed');
      } else {
        throw new Error(result?.error);
      }
    } catch (err) {
      setError('Step 6 failed: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const freezeUploads = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const result = await batchManagementService?.setUploadFreeze(true);
      if (result?.success) {
        setUploadFrozen(true);
        setSuccess('✅ Uploads frozen for safe verification process');
      } else {
        throw new Error(result?.error);
      }
    } catch (err) {
      setError('Failed to freeze uploads: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const restartWorker = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Trigger worker restart via batch requeue
      const result = await batchManagementService?.requeueStuckBatches();
      if (result?.success) {
        setSuccess('✅ Ingest worker restart triggered successfully');
      } else {
        throw new Error(result?.error);
      }
    } catch (err) {
      setError('Failed to restart worker: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyCountsMoving = async () => {
    // Check if we have history to compare
    if (countsHistory?.length < 2) {
      return false;
    }

    const latest = countsHistory?.[countsHistory?.length - 1];
    const previous = countsHistory?.[countsHistory?.length - 2];

    // Check if any key metrics have increased
    return latest?.personsAll > previous?.personsAll || 
           latest?.arrestsAll > previous?.arrestsAll || 
           latest?.arresteeRecordsAll > previous?.arresteeRecordsAll;
  };

  const startStep3Monitoring = () => {
    setIsMonitoring(true);
    setCountsHistory([]);
    setError('');
    setSuccess('Step-2 delta monitoring started - watching for count increases...');
  };

  const stopStep3Monitoring = () => {
    setIsMonitoring(false);
    setStep3Status('waiting');
  };

  const getStep3StatusColor = () => {
    switch (step3Status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'monitoring': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getVerificationProgress = () => {
    const steps = [
      verificationResults?.workerStatus === 'running',
      verificationResults?.batchQueued !== null,
      verificationResults?.promoterHealth !== null,
      verificationResults?.endToEndTest !== null,
      step3Status === 'success',
      verificationResults?.unfreezeReady
    ];
    
    return steps?.filter(Boolean)?.length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-red-600" />
          <div>
            <h2 className="text-2xl font-bold text-red-900">Emergency Verification & Kickstart Plan</h2>
            <p className="text-red-700">
              Complete 6-step verification to get canonical counts moving and enable safe unfreeze
            </p>
            <div className="mt-2">
              <div className="text-sm text-red-600">
                Progress: {getVerificationProgress()}/6 steps completed
              </div>
              <div className="w-full bg-red-200 rounded-full h-2 mt-1">
                <div 
                  className="bg-red-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(getVerificationProgress() / 6) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700 text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700 text-sm font-medium">{success}</span>
          </div>
        </div>
      )}

      {/* Step 0: Worker Process Verification */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">0</span>
          Worker Process Verification
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Worker Status:</div>
            <div className={`font-semibold flex items-center gap-2 ${
              verificationResults?.workerStatus === 'running' ? 'text-green-600' : 
              verificationResults?.workerStatus === 'stopped' ? 'text-red-600' : 'text-gray-600'
            }`}>
              <Activity className="w-4 h-4" />
              {verificationResults?.workerStatus === 'running' ? '✅ Worker Running' : 
               verificationResults?.workerStatus === 'stopped' ? '❌ Worker Stopped' : 
               verificationResults?.workerStatus === 'error' ? '❌ Worker Error' : '❓ Unknown'}
            </div>
          </div>
          
          <button
            onClick={verifyWorkerProcess}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Verify Worker
          </button>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Checks:</strong> A) Worker process up and responding, B) Worker connected to same DB/schema as API/UI
        </div>
      </div>

      {/* Freeze Control */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">⚠</span>
          Upload Freeze Control
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Current Status:</div>
            <div className={`font-semibold ${uploadFrozen ? 'text-red-600' : 'text-green-600'}`}>
              {uploadFrozen === null ? 'Checking...' : uploadFrozen ? '🔒 UPLOADS FROZEN' : '✅ Uploads Active'}
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={freezeUploads}
              disabled={loading || uploadFrozen === true}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {uploadFrozen === true ? 'Already Frozen' : 'Freeze Uploads'}
            </button>
            
            <button
              onClick={safeUnfreeze}
              disabled={loading || uploadFrozen === false || getVerificationProgress() < 5}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Safe Unfreeze
            </button>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Safe unfreeze requires:</strong> Steps 0-4 completed + counts moving + zero stuck batches
        </div>
      </div>

      {/* Step 1: Requeue Known Big Batch */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
          Requeue Big Batch & Restart Worker
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Target Batch:</div>
            <div className="font-medium text-gray-900">
              {targetBatch?.filename || 'MediaArrestLogDatabase.csv (searching...)'}
            </div>
            {verificationResults?.batchQueued && (
              <div className="text-green-600 text-sm">✅ Batch requeued successfully</div>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={requeueTargetBatch}
              disabled={loading || !targetBatch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Requeue & Restart
            </button>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Action:</strong> Requeues MediaArrestLogDatabase.csv and restarts ingest worker to ensure promotion runs
        </div>
      </div>

      {/* Step 2: Delta Monitoring */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
          Delta Monitoring (Counts Must Climb)
        </h3>

        <div className="flex items-center justify-between mb-4">
          <div>
            <div className={`px-3 py-2 rounded-lg text-sm font-medium ${getStep3StatusColor()}`}>
              Status: {step3Status === 'waiting' ? 'Ready to Monitor' : 
                      step3Status === 'monitoring' ? 'Live Monitoring' :
                      step3Status === 'success' ? 'Monitoring Active' : 'Error Handled'}
            </div>
          </div>
          
          <div className="flex gap-2">
            {!isMonitoring ? (
              <button
                onClick={startStep3Monitoring}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Play className="w-4 h-4 inline mr-1" />
                Start Monitor
              </button>
            ) : (
              <button
                onClick={stopStep3Monitoring}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Square className="w-4 h-4 inline mr-1" />
                Stop Monitor
              </button>
            )}
            
            <button
              onClick={fetchCurrentCounts}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* Current Counts Display */}
        {counts && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-600">Persons</span>
              </div>
              <div className="text-lg font-bold text-blue-900">{counts?.personsAll}</div>
            </div>
            
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <FileText className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600">Arrests</span>
              </div>
              <div className="text-lg font-bold text-green-900">{counts?.arrestsAll}</div>
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <Database className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-purple-600">Arrestees</span>
              </div>
              <div className="text-lg font-bold text-purple-900">{counts?.arresteeRecordsAll}</div>
            </div>
            
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <Activity className="w-4 h-4 text-orange-600" />
                <span className="text-xs text-orange-600">Raw Data</span>
              </div>
              <div className="text-lg font-bold text-orange-900">{counts?.ingestRawAll}</div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <Clock className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-yellow-600">Processing</span>
              </div>
              <div className="text-lg font-bold text-yellow-900">{counts?.uploadProcessing}</div>
            </div>
            
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600">Completed</span>
              </div>
              <div className="text-lg font-bold text-green-900">{counts?.uploadCompleted}</div>
            </div>
          </div>
        )}

        {/* Counts History */}
        {countsHistory?.length > 1 && (
          <div className="mt-4">
            <h4 className="font-medium text-gray-900 mb-2">Recent History (Last 10) - Look for increases!</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Persons</th>
                    <th className="px-2 py-1 text-left">Arrests</th>
                    <th className="px-2 py-1 text-left">Arrestees</th>
                    <th className="px-2 py-1 text-left">Raw</th>
                    <th className="px-2 py-1 text-left">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {countsHistory?.slice(-10)?.reverse()?.map((entry, index) => {
                    const prevEntry = countsHistory?.slice(-10)?.reverse()?.[index + 1];
                    const increasing = prevEntry && (
                      entry?.personsAll > prevEntry?.personsAll ||
                      entry?.arrestsAll > prevEntry?.arrestsAll ||
                      entry?.arresteeRecordsAll > prevEntry?.arresteeRecordsAll
                    );
                    
                    return (
                      <tr key={index} className={increasing ? 'bg-green-50' : ''}>
                        <td className="px-2 py-1">{entry?.timestamp?.toLocaleTimeString()}</td>
                        <td className="px-2 py-1">{entry?.personsAll}</td>
                        <td className="px-2 py-1">{entry?.arrestsAll}</td>
                        <td className="px-2 py-1">{entry?.arresteeRecordsAll}</td>
                        <td className="px-2 py-1">{entry?.ingestRawAll}</td>
                        <td className="px-2 py-1">
                          {increasing && <span className="text-green-600">↗️ Increasing</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded mt-4">
          <strong>Healthy:</strong> arrests_all and arrestee_records_all increase; processing dips then rises; completed increases.
          <br />
          <strong>Stuck:</strong> All numbers flat.
        </div>
      </div>

      {/* Step 3: Promoter Sanity Checks */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-yellow-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
          Promoter Sanity Checks
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Data Quality Check:</div>
            <div className="font-medium text-gray-900">
              {batchSanityChecks ? (
                <div className="space-y-1">
                  <div className={`text-sm ${batchSanityChecks?.datetimeHealthy ? 'text-green-600' : 'text-red-600'}`}>
                    {batchSanityChecks?.datetimeHealthy ? '✅' : '❌'} DateTime: {batchSanityChecks?.noDatetime}/{batchSanityChecks?.totalRows} missing
                  </div>
                  <div className={`text-sm ${batchSanityChecks?.nameHealthy ? 'text-green-600' : 'text-red-600'}`}>
                    {batchSanityChecks?.nameHealthy ? '✅' : '❌'} Names: {batchSanityChecks?.badName}/{batchSanityChecks?.totalRows} missing
                  </div>
                  <div className={`text-sm ${batchSanityChecks?.facilityHealthy ? 'text-green-600' : 'text-red-600'}`}>
                    {batchSanityChecks?.facilityHealthy ? '✅' : '❌'} Facilities: {batchSanityChecks?.noFacilityName}/{batchSanityChecks?.totalRows} missing
                  </div>
                </div>
              ) : 'Not checked yet'}
            </div>
          </div>
          
          <button
            onClick={runPromoterSanityChecks}
            disabled={loading || !targetBatch}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Run Checks
          </button>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Fixes Applied:</strong> 
          <br />• Promoter uses COALESCE(booking_datetime, arrest_datetime) 
          <br />• Facility mapping stores facility_text when name→id fails
          <br />• Empty charges allowed (don't block arrest insert)
        </div>
      </div>

      {/* Step 4: End-to-End Test */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
          End-to-End Single Arrest Test
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Promotion Test Results:</div>
            <div className="font-medium text-gray-900">
              {endToEndResults ? (
                <div className="space-y-1">
                  <div className="text-sm text-gray-700">
                    Persons: +{endToEndResults?.personsCreated} created, {endToEndResults?.personsExisting} existing
                  </div>
                  <div className="text-sm text-gray-700">
                    Arrests: +{endToEndResults?.arrestsCreated} created, {endToEndResults?.arrestsExisting} existing
                  </div>
                  <div className={`text-sm font-medium ${endToEndResults?.promotionWorking ? 'text-green-600' : 'text-red-600'}`}>
                    {endToEndResults?.promotionWorking ? '✅ Promotion Working' : '❌ Promotion Failed'}
                  </div>
                </div>
              ) : 'Not tested yet'}
            </div>
          </div>
          
          <button
            onClick={runEndToEndTest}
            disabled={loading || !targetBatch}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Wrench className="w-4 h-4" />
            Test Promotion
          </button>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Test:</strong> Manually call promote_batch_persons() and promote_batch_arrests() to verify transform/upsert functions work
        </div>
      </div>

      {/* Step 5: Step-3 Monitor Verification */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
          Step-3 Monitor Database Trust
        </h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-600">Monitor Status:</div>
            <div className={`font-medium ${step3Status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {step3Status === 'success' ? '✅ Returns JSON, Non-zero Values' : '❌ Issues with endpoint or data'}
            </div>
          </div>
          
          <button
            onClick={verifyStep3Monitor}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Verify Monitor
          </button>
        </div>
        
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Requirements:</strong> Counts endpoint returns 200 + JSON, never HTML. Shows non-zero values when promotion is running.
        </div>
      </div>

      {/* Step 6: Verification Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">6</span>
          ✅ Safe Unfreeze Criteria
        </h3>
        <div className="space-y-2 text-sm text-blue-800">
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${verificationResults?.workerStatus === 'running' ? 'text-green-600' : 'text-gray-400'}`} />
            <span>Worker process verified as running and connected to same DB</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${verificationResults?.batchQueued ? 'text-green-600' : 'text-gray-400'}`} />
            <span>Big batch requeued and worker restarted</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${step3Status === 'success' && verifyCountsMoving() ? 'text-green-600' : 'text-gray-400'}`} />
            <span>Step-3 renders without error and shows persons/arrests rising</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${verificationResults?.promoterHealth?.datetimeHealthy && verificationResults?.promoterHealth?.nameHealthy ? 'text-green-600' : 'text-gray-400'}`} />
            <span>Promoter sanity checks pass (datetime/facility mapping OK)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${verificationResults?.endToEndTest?.promotionWorking ? 'text-green-600' : 'text-gray-400'}`} />
            <span>End-to-end single arrest promotion test succeeds</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${verificationResults?.unfreezeReady ? 'text-green-600' : 'text-gray-400'}`} />
            <span>Watchdog reports 0 stuck batches</span>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
          <div className="text-sm font-medium text-blue-900 mb-2">
            Verification Progress: {getVerificationProgress()}/6 steps completed
          </div>
          {getVerificationProgress() >= 5 ? (
            <div className="text-green-700 text-sm font-medium">
              🎉 Ready for safe unfreeze! All critical checks passed.
            </div>
          ) : (
            <div className="text-blue-700 text-sm">
              Complete the verification steps above before unfreezing uploads.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}